import React from 'react';
import { Helmet } from 'react-helmet';
import PropTypes from 'prop-types';

/**
 * SEO Component - Dynamically sets page metadata for better SEO
 * 
 * Usage:
 * <SEO 
 *   title="Product Name | Premier Pharmaceuticals"
 *   description="Product description here"
 *   keywords="pharmaceutical, medicine, healthcare"
 *   image="https://example.com/image.jpg"
 *   url="https://app.premierpharma.com/product/123"
 * />
 */
const SEO = ({
  title = 'Premier Pharmaceuticals | Quality Pharmaceutical Solutions',
  description = 'Premier Pharmaceuticals offers high-quality pharmaceutical products and solutions. Browse our extensive catalog of healthcare products with fast, reliable shipping.',
  keywords = 'pharmaceuticals, pharmacy, healthcare, medical supplies, Premier Pharmaceuticals',
  image = 'https://premierpharma-production.s3.us-west-2.amazonaws.com/icons/premier-pharma-dp.svg',
  url = 'https://app.premierpharma.com/',
  type = 'website',
  author = 'Premier Pharmaceuticals',
  schema = null
}) => {
  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content={author} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={url} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      
      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={url} />
      <meta property="twitter:title" content={title} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content={image} />
      
      {/* Structured Data (Schema.org) */}
      {schema && (
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      )}
    </Helmet>
  );
};

SEO.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  keywords: PropTypes.string,
  image: PropTypes.string,
  url: PropTypes.string,
  type: PropTypes.string,
  author: PropTypes.string,
  schema: PropTypes.object
};

export default SEO;

/**
 * Example Schema.org structured data for a Product page:
 * 
 * const productSchema = {
 *   "@context": "https://schema.org/",
 *   "@type": "Product",
 *   "name": "Product Name",
 *   "image": "https://example.com/product-image.jpg",
 *   "description": "Product description",
 *   "brand": {
 *     "@type": "Brand",
 *     "name": "Premier Pharmaceuticals"
 *   },
 *   "offers": {
 *     "@type": "Offer",
 *     "url": "https://app.premierpharma.com/product/123",
 *     "priceCurrency": "USD",
 *     "price": "29.99",
 *     "availability": "https://schema.org/InStock"
 *   }
 * };
 * 
 * <SEO 
 *   title="Product Name | Premier Pharmaceuticals"
 *   description="Product description"
 *   schema={productSchema}
 * />
 */

/**
 * Example Schema.org structured data for Organization (can be added to homepage):
 * 
 * const organizationSchema = {
 *   "@context": "https://schema.org",
 *   "@type": "Organization",
 *   "name": "Premier Pharmaceuticals",
 *   "url": "https://app.premierpharma.com",
 *   "logo": "https://premierpharma-production.s3.us-west-2.amazonaws.com/icons/premier-pharma-dp.svg",
 *   "description": "Premier Pharmaceuticals offers high-quality pharmaceutical products and solutions.",
 *   "sameAs": [
 *     "https://www.facebook.com/people/Premier-Pharma/100092650552829",
 *     "https://www.instagram.com/premierpharma_/",
 *     "https://www.linkedin.com/company/premier-pharmaceuticals"
 *   ]
 * };
 */
